import { NavLink } from "react-router-dom";
import {
  LayoutGrid,
  Search,
  Briefcase,
  Landmark,
  ShieldCheck,
  TrendingUp,
  GraduationCap,
  Trophy,
  LineChart,
  Menu,
  Target,
  BookOpen,
  Calculator as CalcIcon,
  Receipt,
  History,
} from "lucide-react";
import { Logo, Wordmark } from "@/components/Logo";
import { cn } from "@/lib/cn";
import type { ReactNode } from "react";

type NavItem = {
  to: string;
  label: string;
  icon: ReactNode;
  badge?: string | number;
  proOnly?: boolean;
};

const PRIMARY_NAV: NavItem[] = [
  { to: "/", label: "Dashboard", icon: <LayoutGrid size={16} /> },
  { to: "/marketplace", label: "Trh nemovitostí", icon: <Search size={16} /> },
  { to: "/portfolio", label: "Moje portfolio", icon: <Briefcase size={16} /> },
  { to: "/banks", label: "Banky & hypotéky", icon: <Landmark size={16} /> },
  { to: "/calculator", label: "Kalkulačka", icon: <CalcIcon size={16} /> },
  { to: "/insurance", label: "Pojišťovna", icon: <ShieldCheck size={16} /> },
  { to: "/stocks", label: "Akcie & ETF", icon: <TrendingUp size={16} /> },
  { to: "/accounting", label: "Účetnictví", icon: <Receipt size={16} /> },
  { to: "/events", label: "Historie", icon: <History size={16} /> },
];

const GROWTH_NAV: NavItem[] = [
  { to: "/academy", label: "Akademie", icon: <GraduationCap size={16} />, proOnly: true },
  { to: "/achievements", label: "Úspěchy", icon: <Trophy size={16} />, proOnly: true },
  { to: "/analytics", label: "Analytika", icon: <LineChart size={16} />, badge: "Pro", proOnly: true },
  { to: "/leaderboard", label: "Žebříček", icon: <Menu size={16} /> },
  { to: "/challenges", label: "Výzvy", icon: <Target size={16} />, proOnly: true },
  { to: "/glossary", label: "Slovník", icon: <BookOpen size={16} /> },
];

type SidebarProps = {
  footer?: ReactNode;
  className?: string;
  /** Fires after each nav click — mobile drawer uses it to close itself. */
  onNavigate?: () => void;
};

export function Sidebar({ footer, className, onNavigate }: SidebarProps) {
  return (
    <aside
      className={cn(
        "flex h-screen w-full flex-col lg:w-[244px] lg:shrink-0",
        "border-r border-stroke bg-gradient-to-b from-bg-raised via-bg-raised to-bg",
        "lg:sticky lg:top-0 px-3.5 py-5",
        className,
      )}
    >
      {/* Brand */}
      <div className="mb-4 flex items-center gap-3 border-b border-stroke px-1.5 pb-5">
        <Logo size={40} />
        <Wordmark />
      </div>

      {/* Navigation */}
      <nav className="-mx-1 flex flex-1 flex-col gap-px overflow-y-auto px-1">
        {PRIMARY_NAV.map((item) => (
          <SidebarLink key={item.to} item={item} onNavigate={onNavigate} />
        ))}

        <div className="px-2.5 pb-1.5 pt-4 text-[10px] font-semibold uppercase tracking-[0.22em] text-ink-dim">
          Rozvoj
        </div>

        {GROWTH_NAV.map((item) => (
          <SidebarLink key={item.to} item={item} onNavigate={onNavigate} />
        ))}
      </nav>

      {footer && <div className="mt-4 border-t border-stroke pt-4">{footer}</div>}
    </aside>
  );
}

function SidebarLink({ item, onNavigate }: { item: NavItem; onNavigate?: (() => void) | undefined }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "relative flex items-center gap-3 rounded-lg px-2.5 py-2.5 text-[13.5px]",
          "text-ink-muted transition-colors",
          "hover:bg-surface hover:text-ink",
          isActive && [
            "bg-amber-dim text-amber-bright",
            "before:absolute before:-left-[14px] before:top-2.5 before:bottom-2.5",
            "before:w-0.5 before:rounded-r",
            "before:bg-gradient-to-b before:from-amber before:to-coral",
          ].join(" "),
        )
      }
    >
      {({ isActive }) => (
        <>
          <span className="shrink-0 opacity-90">{item.icon}</span>
          <span className="flex-1 truncate">{item.label}</span>
          {item.badge && (
            <span
              className={cn(
                "mono rounded-lg px-1.5 py-0.5 text-[10px] font-medium",
                isActive ? "bg-amber text-[#10141d]" : "bg-surface-raised text-ink-muted",
              )}
            >
              {item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}
